import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { SetAttrsStep } from '@atlaskit/adf-schema/steps';
import { tableCellMinWidth, tableNewColumnMinWidth } from '@atlaskit/editor-common/styles';
import { akEditorDefaultLayoutWidth } from '@atlaskit/editor-shared-styles';
import { TableMap } from '@atlaskit/editor-tables/table-map';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { hasTableBeenResized } from '../pm-plugins/table-resizing/utils';
import { isMinCellWidthTable } from '../pm-plugins/table-resizing/utils/colgroup';
import { getTableMaxWidth } from '../pm-plugins/table-resizing/utils/misc';
import { getResizeState, normaliseTableLayout } from '../pm-plugins/table-resizing/utils/resize-state';
import { scaleTableTo } from '../pm-plugins/table-resizing/utils/scale-table';
import { insertColumnButtonOffset } from '../ui/common-styles';
export var updateColumnWidths = function updateColumnWidths(resizeState, table, start) {
  return function (tr) {
    var map = TableMap.get(table);
    var updatedCellsAttrs = {};

    // calculating new attributes for each cell
    for (var columnIndex = 0; columnIndex < map.width; columnIndex++) {
      for (var rowIndex = 0; rowIndex < map.height; rowIndex++) {
        var width = resizeState.cols[columnIndex].width;
        var mapIndex = rowIndex * map.width + columnIndex;
        var cellPos = map.map[mapIndex];
        var attrs = updatedCellsAttrs[cellPos] || _objectSpread({}, table.nodeAt(cellPos).attrs);
        var colspan = attrs.colspan || 1;
        if (attrs.colwidth && attrs.colwidth.length > colspan) {
          attrs.colwidth = attrs.colwidth.slice(0, colspan);
        }

        // Rowspanning cell that has already been handled
        if (rowIndex && map.map[mapIndex] === map.map[mapIndex - map.width]) {
          continue;
        }
        var colspanIndex = colspan === 1 ? 0 : columnIndex - map.colCount(cellPos);
        if (attrs.colwidth && attrs.colwidth[colspanIndex] === width) {
          continue;
        }
        var colwidths = attrs.colwidth ? attrs.colwidth.slice() : Array.from({
          length: colspan
        }, function (_) {
          return 0;
        });
        colwidths[colspanIndex] = width;
        if (colwidths.length > colspan) {
          colwidths = colwidths.slice(0, colspan);
        }
        updatedCellsAttrs[cellPos] = _objectSpread(_objectSpread({}, attrs), {}, {
          colwidth: colwidths.includes(0) ? undefined : colwidths
        });
      }
    }

    // updating all cells with new attributes
    if (getBooleanFF('platform.editor.update-table-cell-width-via-step')) {
      var seen = {};
      for (var _rowIndex = 0; _rowIndex < map.height; _rowIndex++) {
        for (var _columnIndex = 0; _columnIndex < map.width; _columnIndex++) {
          var _mapIndex = _rowIndex * map.width + _columnIndex;
          var pos = map.map[_mapIndex];
          var cell = table.nodeAt(pos);
          if (!seen[pos] && cell) {
            if (updatedCellsAttrs[pos]) {
              tr.step(new SetAttrsStep(pos + start, updatedCellsAttrs[pos]));
            }
            seen[pos] = true;
          }
        }
      }
    } else {
      var rows = [];
      var _seen = {};
      for (var _rowIndex2 = 0; _rowIndex2 < map.height; _rowIndex2++) {
        var row = table.child(_rowIndex2);
        var cells = [];
        for (var _columnIndex2 = 0; _columnIndex2 < map.width; _columnIndex2++) {
          var _mapIndex2 = _rowIndex2 * map.width + _columnIndex2;
          var _pos = map.map[_mapIndex2];
          var _cell = table.nodeAt(_pos);
          if (!_seen[_pos] && _cell) {
            cells.push(_cell.type.createChecked(updatedCellsAttrs[_pos] || _cell.attrs, _cell.content, _cell.marks));
            _seen[_pos] = true;
          }
        }
        rows.push(row.type.createChecked(row.attrs, cells, row.marks));
      }
      var tablePos = start - 1;
      var selectionBookmark = tr.selection.getBookmark();
      tr.replaceWith(tablePos, tablePos + table.nodeSize, table.type.createChecked(table.attrs, rows, table.marks));
      /**
       * We want to restore to the original selection but w/o applying the mapping. Function
       * tr.replaceWith puts the selection after the inserted content. We need to manually
       * set the selection back to original state. Mapping in this case doesn't quite work
       * e.g. if we change the content before a selection. This is because mapping
       * means moving it if the content in front of it changed. Instead we can get
       * bookmark of selection.
       *
       * @see https://github.com/ProseMirror/prosemirror/issues/645
       */
      tr.setSelection(selectionBookmark.resolve(tr.doc));
    }
    return tr;
  };
};

/**
 * This function is called when user inserts/deletes a column in a table to;
 * - rescale all columns (if the table did not overflow before the insertion)
 * - and update column widths.
 *
 * This is done manually to avoid a multi-dispatch in TableComponent. See [ED-8288].
 * @param table
 * @param view
 * @returns Updated transaction with rescaled columns for a given table
 */
export var rescaleColumns = function rescaleColumns(getEditorContainerWidth) {
  return function (table, view) {
    return function (tr) {
      var _tableRef$parentEleme;
      if (!view) {
        return tr;
      }
      var pos = table.pos;
      var newTable = tr.doc.nodeAt(pos);
      var state = view.state;
      var domAtPos = view.domAtPos.bind(view);
      var maybeTable = domAtPos(table.start).node;
      var tableRef = maybeTable.closest('table');
      if (!tableRef || !newTable) {
        return tr;
      }
      var layout = normaliseTableLayout(tableRef === null || tableRef === void 0 ? void 0 : tableRef.dataset.layout);
      // The is the width the table can reach before overflowing
      var maxSize = getTableMaxWidth({
        table: table.node,
        tableStart: table.start,
        state: state,
        layout: layout,
        getEditorContainerWidth: getEditorContainerWidth
      });
      var tableWidth = tableRef.clientWidth || akEditorDefaultLayoutWidth;
      var tableMaxWidth = (tableRef === null || tableRef === void 0 ? void 0 : (_tableRef$parentEleme = tableRef.parentElement) === null || _tableRef$parentEleme === void 0 ? void 0 : _tableRef$parentEleme.clientWidth) || 0;
      tableMaxWidth -= insertColumnButtonOffset;
      var newTableMap = TableMap.get(newTable);
      var noOfColumns = newTableMap.width;
      if (!noOfColumns || noOfColumns <= 0) {
        return tr;
      }
      var columnWidthUnresized = tableWidth / noOfColumns;

      // If the table has not been resized, and the column width is bigger than the minimum column width
      // we skip updating the size of columns here.
      if (!hasTableBeenResized(table.node) && columnWidthUnresized > tableCellMinWidth) {
        return tr;
      }

      // If the table has not been resized, and the column width is smaller than the minimum column width
      // Or if the table has been resized, but each column width is either 48px or null
      // we update the table to have 48px for each column
      if (!hasTableBeenResized(table.node) && columnWidthUnresized <= tableCellMinWidth || hasTableBeenResized(table.node) && isMinCellWidthTable(table.node)) {
        var widths = new Array(noOfColumns).fill(tableCellMinWidth);
        var cols = widths.map(function (_, index) {
          return {
            width: tableCellMinWidth,
            minWidth: tableCellMinWidth,
            index: index
          };
        });
        var overflow = tableWidth > maxSize;
        var minWidthResizeState = {
          cols: cols,
          widths: widths,
          maxSize: maxSize,
          tableWidth: tableWidth,
          overflow: overflow
        };
        return updateColumnWidths(minWidthResizeState, table.node, table.start)(tr);
      }
      var resizeState = getResizeState({
        minWidth: tableCellMinWidth,
        table: table.node,
        start: table.start,
        tableRef: tableRef,
        domAtPos: domAtPos,
        maxSize: maxSize
      });
      var previousTableWidth = resizeState.tableWidth - tableNewColumnMinWidth;
      var tableDidntPreviouslyOverflow = previousTableWidth <= Math.max(maxSize, tableMaxWidth);

      // If the new table width will result in the table going into an overflow state
      // we resize the cells to avoid the overflow occuring
      if (tableDidntPreviouslyOverflow && resizeState.overflow) {
        resizeState = scaleTableTo(resizeState, maxSize);
      }
      return updateColumnWidths(resizeState, table.node, table.start)(tr);
    };
  };
};